import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Smartphone, Download, Monitor, Apple, Bot, Send, Clock, Loader2, BellRing } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <div><div className="text-2xl font-bold leading-none">{value ?? "—"}</div><div className="text-xs text-muted-foreground mt-1">{label}</div></div>
    </Card>
  );
}

/** Admin dashboard section: PWA installs counter + daily digest push controls. */
export default function AdminPwaStats() {
  const [s, setS] = useState(null);
  const [digest, setDigest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hour, setHour] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([axios.get(`${API}/admin/pwa/stats`), axios.get(`${API}/push/admin/digest/status`)]);
      setS(a.data); setDigest(b.data); setHour(String(b.data.hour_utc));
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const saveDigest = async (patch) => {
    setBusy(true);
    try { const r = await axios.put(`${API}/push/admin/digest/settings`, patch); setDigest(r.data); toast.success("Configuração salva"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };
  const sendNow = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/push/admin/digest/send-now`); toast.success(`Resumo enviado: ${r.data.sent} enviados · ${r.data.skipped} pulados · ${r.data.failed} falhas`); load(); }
    catch { toast.error("Erro ao enviar"); }
    setBusy(false);
  };

  const bp = s?.by_platform || {};
  const localHour = (h) => `${String(((Number(h) - 3) + 24) % 24).padStart(2, "0")}:00 (Brasília)`;

  return (
    <section data-testid="stats-pwa-group" className="space-y-3">
      <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold">App instalado (PWA) & Push</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Download}   label="Instalações (total)"     value={s?.installs_total}         color="bg-blue-500/10 text-blue-600"     testid="pwa-installs-total" />
        <Stat icon={Smartphone} label="Instalações (7 dias)"     value={s?.installs_7d}            color="bg-emerald-500/10 text-emerald-600" testid="pwa-installs-7d" />
        <Stat icon={Monitor}    label="Abertos em tela cheia"    value={s?.standalone_devices}     color="bg-indigo-500/10 text-indigo-600" testid="pwa-standalone-devices" />
        <Stat icon={Bot}        label="Prompt recusado"          value={s?.prompt_dismissed}       color="bg-amber-500/10 text-amber-600"   testid="pwa-dismissed" />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="pwa-platforms">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted"><Bot className="w-3 h-3" /> Android {bp.android || 0}</span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted"><Apple className="w-3 h-3" /> iOS {(bp.ios || 0) + (s?.ios_standalone_devices || 0)}</span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted"><Monitor className="w-3 h-3" /> Desktop {bp.desktop || 0}</span>
        {s?.accept_rate != null && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted">Taxa de aceite {s.accept_rate}%</span>}
        <span className="ml-auto">iOS não tem prompt: contamos a 1ª abertura em tela cheia como instalação.</span>
      </div>

      <Card className="p-4" data-testid="pwa-installs-chart-card">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="font-semibold text-sm">Instalações por dia · últimos 30 dias</div>
            <div className="text-xs text-muted-foreground">Dispositivos que instalaram (prompt aceito/instalado) e aberturas em tela cheia</div>
          </div>
          <div className="text-xs text-muted-foreground" data-testid="pwa-installs-30d">{s?.installs_30d ?? 0} instalações em 30 dias</div>
        </div>
        <div className="h-52" data-testid="pwa-installs-chart">
          {s?.daily ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.daily.map((d) => ({ ...d, label: d.day.slice(8, 10) + "/" + d.day.slice(5, 7) }))} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip labelFormatter={(l) => `Dia ${l}`} formatter={(v, n) => [v, n === "installs" ? "Instalações" : "Tela cheia"]} />
                <Legend formatter={(v) => (v === "installs" ? "Instalações" : "Aberturas em tela cheia")} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="installs" fill="#2563eb" radius={[4, 4, 0, 0]} />
                <Bar dataKey="standalone" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="flex items-center justify-center h-full text-xs text-muted-foreground">Carregando…</div>}
        </div>
      </Card>

      <Card className="p-4" data-testid="digest-admin-card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-600 flex items-center justify-center"><BellRing className="w-5 h-5" /></div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold text-sm">Resumo diário por push</div>
            <div className="text-xs text-muted-foreground">Missões do dia + moedas ganhas ontem, enviado 1×/dia para quem ativou o push. {digest ? `${digest.subscribed_users} usuário(s) inscritos · ${digest.sent_today} enviados hoje` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <Input type="number" min={0} max={23} value={hour} onChange={(e) => setHour(e.target.value)} className="w-16 h-8" data-testid="digest-hour-input" />
            <span className="text-xs text-muted-foreground">h UTC = {hour !== "" ? localHour(hour) : ""}</span>
            <Button size="sm" variant="outline" disabled={busy || hour === "" || Number(hour) === digest?.hour_utc} onClick={() => saveDigest({ hour_utc: Number(hour) })} data-testid="digest-hour-save">Salvar</Button>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!digest?.enabled} disabled={busy || !digest} onCheckedChange={(v) => saveDigest({ enabled: v })} data-testid="digest-enabled-toggle" />
            <span className="text-xs">{digest?.enabled ? "Ativo" : "Pausado"}</span>
          </div>
          <Button size="sm" onClick={sendNow} disabled={busy} data-testid="digest-send-now">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Enviar agora para todos</>}
          </Button>
        </div>
        {digest?.last?.length > 0 && (
          <div className="mt-3 text-xs text-muted-foreground space-y-0.5" data-testid="digest-last-list">
            {digest.last.map((d) => <div key={`${d.user_id}-${d.day}`} className="truncate">• {new Date(d.sent_at).toLocaleString("pt-BR")} — {d.text}</div>)}
          </div>
        )}
      </Card>
    </section>
  );
}
